import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PlugZap, Copy, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ligne = ({ ok, libelle, detail }) => (
  <li className="flex items-start gap-2 text-sm">
    {ok ? (
      <CheckCircle2 size={15} className="text-[#0A5C36] shrink-0 mt-0.5" />
    ) : (
      <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
    )}
    <span className="text-stone-700">
      {libelle}
      {detail ? <span className="text-stone-500"> — {detail}</span> : null}
    </span>
  </li>
);

/** État de la passerelle CinetPay + IP à inscrire dans leur liste blanche. */
export const PasserellePaiementCard = ({ adminId }) => {
  const [etat, setEtat] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/paiements/diagnostic`, { params: { admin_id: adminId } })
      .then((r) => setEtat(r.data))
      .catch(() => {});
  }, [adminId]);

  if (!etat) return null;

  const copier = () => {
    navigator.clipboard?.writeText(etat.ip_publique_a_autoriser);
    toast.success("IP copiée", { description: "À coller dans CinetPay > API & sécurité > Liste Blanche IP" });
  };

  const joignable = etat.authentification_cinetpay;

  return (
    <div className="mt-6 bg-white border border-[#E5DFD5] rounded-2xl p-5" data-testid="passerelle-paiement-card">
      <h3 className="font-heading text-lg font-extrabold text-stone-900 flex items-center gap-2">
        <PlugZap size={18} className="text-[#0A5C36]" /> Passerelle CinetPay
      </h3>

      <ul className="mt-4 space-y-2">
        <Ligne ok={etat.cle_api_configuree} libelle="Clé API renseignée" />
        <Ligne ok={etat.authentification_cinetpay} libelle="Authentification CinetPay" detail={etat.detail_authentification} />
        <Ligne
          ok={etat.mot_de_passe_api_configure}
          libelle="Mot de passe API"
          detail={etat.mot_de_passe_api_configure ? "configuré" : "à renseigner"}
        />
        <Ligne ok={etat.paiement_reel_actif} libelle="Encaissement réel activé" />
        <Ligne
          ok={etat.proxy_ip_fixe_actif}
          libelle="IP de sortie fixe (proxy Fixie)"
          detail={etat.proxy_ip_fixe_actif ? "active" : "absente — l'IP change à chaque déploiement"}
        />
        <Ligne
          ok={joignable}
          libelle={etat.api || "API CinetPay"}
          detail={joignable ? "connexion établie" : "connexion refusée — vérifiez la Liste Blanche IP"}
        />
      </ul>

      <div className="mt-5 bg-[#FAF8F5] border border-[#E5DFD5] rounded-xl p-4">
        {etat.proxy_ip_fixe?.en_quarantaine && (
          <div className="mb-4 rounded-xl border-2 border-red-200 bg-red-50 p-3"
            data-testid="passerelle-proxy-hs">
            <p className="text-xs font-extrabold uppercase tracking-wider text-red-700">
              Proxy à IP fixe hors service
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-red-700">
              Motif renvoyé : « {etat.proxy_ip_fixe.motif || "connexion refusée"} ». Les appels
              sortent donc par l'IP directe du serveur
              {etat.ip_directe_du_serveur ? ` (${etat.ip_directe_du_serveur})` : ""} :
              <strong> autorisez cette adresse chez CinetPay pour rétablir les paiements
              immédiatement</strong>, ou renouvelez l'identifiant du proxy (FIXIE_PROXY_URL).
            </p>
          </div>
        )}
        <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
          IP à autoriser chez CinetPay
        </p>
        <div className="flex items-center gap-2 mt-2">
          <code
            className="text-base font-bold text-stone-900 bg-white border border-[#E5DFD5] rounded-lg px-3 py-1.5"
            data-testid="passerelle-ip"
          >
            {etat.ip_publique_a_autoriser}
          </code>
          <button
            onClick={copier}
            data-testid="passerelle-copier-ip"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-stone-900 hover:bg-stone-700 rounded-full px-3 py-2 transition-colors"
          >
            <Copy size={13} /> Copier
          </button>
        </div>
        <p className="text-xs text-stone-500 mt-3 leading-relaxed">
          CinetPay → Ressources → API &amp; sécurité → <strong>Liste Blanche IP</strong> → ajouter cette adresse.
        </p>
      </div>
    </div>
  );
};
